/**
 * When the rail's hover flyout opens and closes. Resting on a closed token's
 * row opens its panel after `open` ms, so sweeping the pointer across the
 * rail flashes nothing; once a panel is shown, moving to another row switches
 * at once. Leaving the row or the panel closes it after `close` ms, which
 * lets a diagonal move from the row into the panel keep it. A pick or Escape
 * closes it at once.
 */
export interface FlyoutDelays {
  open: number;
  close: number;
}

export const FLYOUT_DELAYS: FlyoutDelays = { open: 150, close: 200 };

export interface FlyoutTimer<T> {
  /** the pointer is on the row of token `id` (a closed one); `value` is what to show for it */
  enterRow: (id: string, value: T) => void;
  /** the pointer is on the panel */
  enterPanel: () => void;
  /** the pointer left a row or the panel */
  leave: () => void;
  /** close now (a pick, Escape, a click on the row) */
  close: () => void;
  /** the token shown, if any */
  shown: () => string | null;
}

/** `onChange` receives the value of the row being shown, or null once closed. */
export function flyoutTimer<T>(
  onChange: (value: T | null) => void,
  delays: FlyoutDelays = FLYOUT_DELAYS,
): FlyoutTimer<T> {
  let shown: string | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cancel = () => {
    clearTimeout(timer);
    timer = undefined;
  };
  const show = (id: string | null, value: T | null) => {
    cancel();
    if (shown === id) return;
    shown = id;
    onChange(value);
  };
  return {
    enterRow: (id, value) => {
      cancel();
      if (shown === id) return;
      if (shown !== null) show(id, value);
      else timer = setTimeout(() => show(id, value), delays.open);
    },
    enterPanel: cancel,
    leave: () => {
      cancel();
      if (shown !== null) timer = setTimeout(() => show(null, null), delays.close);
    },
    close: () => show(null, null),
    shown: () => shown,
  };
}
